import * as React from 'react';
import { connect } from 'react-redux';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import SidePanelActions from './side-panel/SidePanelActions';
import SidePanelAttachments from './side-panel/SidePanelAttachments';
import SidePanelLinks from './side-panel/SidePanelLinks';
import SidePanelNotes from './side-panel/SidePanelNotes';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import type { Campaign, Subcampaign } from 'types';

type OwnProps = {
  campaign: Campaign,
  subcampaign: Subcampaign,
  isMessengerDisabled: boolean,
  onSizeChangeRequest: (a: 'small' | 'normal') => void
};

type Props = {
  dispatch: any
} & OwnProps;

type State = {
  panels: Array<string>
};

class MessengerSidePanels extends React.Component<Props, State> {
  state = {
    panels: []
  };

  openSidePanel = (name: string) => {
    if (name === 'notes') {
      const { onSizeChangeRequest } = this.props;
      onSizeChangeRequest('small');
    }

    this.setState(state => ({
      panels: [...state.panels, name]
    }));
  };

  handleBeforeClose = (callback: () => void) => {
    const { dispatch } = this.props;
    const prompt = document.querySelector('.js-prompt-exit-panel');

    if (prompt && prompt instanceof HTMLElement && prompt.dataset.message) {
      // ask the user
      dispatch(showConfirmModal({
        title: decodeURIComponent(prompt.dataset.title ?? ''),
        message: decodeURIComponent(prompt.dataset.message),
        onConfirm: () => { // close the panel
          dispatch(hideConfirmModal());
          callback();
        },
        // keep showing the panel
        onCancel: () => {
          dispatch(hideConfirmModal());
        }
      }));
    } else {
      // close the panel (default behavior)
      callback();
    }
  };

  handleTopSidePanel = () => {
    const { onSizeChangeRequest } = this.props;
    onSizeChangeRequest('normal');

    this.setState(state => ({
      panels: state.panels.slice(0, -1)
    }));
  };

  handleAllSidePanels = () => {
    const { onSizeChangeRequest } = this.props;
    onSizeChangeRequest('normal');

    this.setState({
      panels: []
    });
  };

  closeTopSidePanel = () => {
    this.handleBeforeClose(this.handleTopSidePanel);
  };

  closeAllSidePanels = () => {
    this.handleBeforeClose(this.handleAllSidePanels);
  };

  renderPanel = (name: string): JSX.Element | null => {
    const { campaign, subcampaign, isMessengerDisabled } = this.props;
    const key = `messenger-side-panel-${name}`;

    switch (name) {
      case 'actions':
        return (
          <SidePanelActions
            key={key}
            campaign={campaign}
            subcampaign={subcampaign}
            isMessengerDisabled={isMessengerDisabled}
            onClosePanel={this.closeTopSidePanel}
            onOpenPanel={this.openSidePanel}
          />
        );
      case 'attachments':
        return (
          <SidePanelAttachments
            key={key}
            subcampaign={subcampaign}
            onClosePanel={this.closeTopSidePanel}
            onCloseAllPanels={this.closeAllSidePanels}
          />
        );
      case 'links':
        return (
          <SidePanelLinks
            key={key}
            onClosePanel={this.closeTopSidePanel}
            onCloseAllPanels={this.closeAllSidePanels}
          />
        );
      case 'notes':
        return (
          <SidePanelNotes
            key={key}
            campaign={campaign}
            subcampaign={subcampaign}
            onClosePanel={this.closeTopSidePanel}
            onCloseAllPanels={this.closeAllSidePanels}
          />
        );
      default:
        return null;
    }
  };

  render(): React.ReactNode {
    const { panels } = this.state;

    return (
      <React.Fragment>
        {
          panels.length === 0 && (
            <div className="messenger-side-panels__pull-tab" onClick={() => this.openSidePanel('actions')}>
              <SVGDynamicIcon
                name="three-dots-vertical-custom-fg"
                customColor="grey"
                height={20}
                width={20}
                clickable
              />
            </div>
          )
        }
        <div className="messenger-side-panels">
          {panels.map(this.renderPanel)}
        </div>
      </React.Fragment>
    );
  }
}

export default connect()(MessengerSidePanels);
